import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Copy, Check, Trash2, Pause, Play, AlertTriangle, Link2, Percent, Pencil } from 'lucide-react';
import { api } from '../services/apiService';
import SearchableDropdown from './SearchableDropdown';
import { money } from './finance/financeFormat';
import { RubyLoader } from './RubyLoader';
import { RubyGemMark } from './RubyGemMark';

/**
 * PARTNER DEALS + COMMISSIONS — the admin half of the partner offer system.
 *
 * A partner used to hold ONE `assignedDealId`, which could not express "market
 * this launch offer to ten shops until October, and this starter to everyone".
 * Now they hold a LINK PER DEAL, each with its own code, seat cap and expiry.
 *
 * SEATS COUNT PAID CONVERSIONS, never signups — so an abandoned signup costs
 * the partner nothing. The consequence is stated on screen rather than hidden:
 * because seats burn at payment, several signups can be in flight against the
 * last seat, so a link can slightly oversell.
 *
 * The two halves render independently (`sections`) because the detail page puts
 * them on different tabs. Default is both, so the panel still stands alone.
 *
 * THE STANDING LINK IS EDITED HERE TOO (2026-09-01). It used to be described by
 * three separate cards elsewhere on the page — its code, its deal, its
 * commission — which read as a second, competing referral system next to this
 * table. Its deal now hangs off the row that already represents it.
 */

const dateStr = (ms?: number) =>
  typeof ms === 'number' ? new Date(ms).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const STATUS_CHIP: Record<string, { cls: string; label: string }> = {
  active: { cls: 'bg-emerald-100 text-emerald-700', label: 'Live' },
  paused: { cls: 'bg-stone-200 text-stone-600', label: 'Paused' },
  expired: { cls: 'bg-amber-100 text-amber-700', label: 'Expired' },
  exhausted: { cls: 'bg-red-100 text-red-700', label: 'Fully claimed' },
};

const PERIODS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'year_to_date', label: 'Year to date' },
  { key: 'all', label: 'All time' },
] as const;
type PeriodKey = (typeof PERIODS)[number]['key'];

function resolveRange(key: PeriodKey, now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (key) {
    case 'this_month': return { fromMs: new Date(y, m, 1).getTime(), toMs: new Date(y, m + 1, 1).getTime() };
    case 'last_month': return { fromMs: new Date(y, m - 1, 1).getTime(), toMs: new Date(y, m, 1).getTime() };
    // To the START OF TOMORROW, so today's earnings are included.
    case 'year_to_date': return { fromMs: new Date(y, 0, 1).getTime(), toMs: new Date(y, m, now.getDate() + 1).getTime() };
    default: return { fromMs: undefined, toMs: undefined };
  }
}

export interface PartnerDealsPanelProps {
  affiliateId: string;
  /** Which halves to render. Defaults to both. */
  sections?: Array<'deals' | 'commissions'>;
  /** What the standing referral link currently sells, shown on its own row. */
  standingDealLabel?: string | null;
  /** Opens the parent's deal picker for the standing link. */
  onChangeStandingDeal?: () => void;
  /** Commission rate the partner earns outside a per-link plan. */
  standingCommissionLabel?: string | null;
  /** Opens the parent's commission editor. */
  onEditCommission?: () => void;
}

const PartnerDealsPanel: React.FC<PartnerDealsPanelProps> = ({
  affiliateId,
  sections = ['deals', 'commissions'],
  standingDealLabel,
  onChangeStandingDeal,
  standingCommissionLabel,
  onEditCommission,
}) => {
  const showDeals = sections.includes('deals');
  const showCommissions = sections.includes('commissions');
  const [links, setLinks] = useState<any[]>([]);
  const [deals, setDeals] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [copied, setCopied] = useState('');

  const [adding, setAdding] = useState(false);
  const [newDealId, setNewDealId] = useState('');
  const [newMax, setNewMax] = useState('');
  const [newExpiry, setNewExpiry] = useState('');
  const [newPlanId, setNewPlanId] = useState('');

  /**
   * The link being edited, if any. Seats, expiry and commission plan could only
   * be chosen at creation — after that the only ways to change a link were to
   * pause it or delete it, and delete is refused once it has conversions. So a
   * link that ran out of seats was unfixable.
   *
   * The deal itself is deliberately NOT editable: the code, the seats consumed
   * and the commissions already booked all hang off that deal, so "change the
   * deal" is a different link, not an edit. Delete and re-add, or add a second.
   */
  const [editing, setEditing] = useState<any | null>(null);
  const [editMax, setEditMax] = useState('');
  const [editExpiry, setEditExpiry] = useState('');
  const [editPlanId, setEditPlanId] = useState('');

  const [period, setPeriod] = useState<PeriodKey>('this_month');
  const [ledger, setLedger] = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res: any = await api.affiliates.admin.listLinks(affiliateId);
      setLinks(res?.links || []);
    } catch (e: any) {
      setError(e?.message || 'Could not load this partner’s deals');
    } finally {
      setLoading(false);
    }
  }, [affiliateId]);

  useEffect(() => { load(); }, [load]);

  // Catalogs for the pickers. Independent so one failure does not blank both.
  useEffect(() => {
    let off = false;
    // 'true' is the string this table stores in its GSI key attribute — see
    // the deals table's ActiveIndex; a boolean would match nothing.
    api.deals.list('true')
      .then((r: any) => { if (!off) setDeals(r?.deals || []); })
      .catch(() => { /* picker shows empty */ });
    import('../services/billingApi')
      .then(({ billingApi }) => billingApi.listCommissionPlans())
      .then((r: any) => { if (!off) setPlans(r?.items || []); })
      .catch(() => { /* optional */ });
    return () => { off = true; };
  }, []);

  const loadLedger = useCallback(async () => {
    // Not rendered on the deals tab — do not pay for the ledger there.
    if (!showCommissions) return;
    try {
      const res: any = await api.affiliates.admin.getCommissions(affiliateId, resolveRange(period));
      setLedger(res);
    } catch {
      setLedger(null);
    }
  }, [affiliateId, period, showCommissions]);

  useEffect(() => { loadLedger(); }, [loadLedger]);

  const copy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 1600);
    } catch {
      setError(`Copy is blocked here — the code is ${text}`);
    }
  };

  const create = async () => {
    if (!newDealId) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const deal = deals.find((d) => d.id === newDealId);
      const res: any = await api.affiliates.admin.createLink(affiliateId, {
        dealId: newDealId,
        dealCode: deal?.code,
        label: deal?.name,
        // '' means unlimited here; 0 is a real value meaning no seats.
        maxUses: newMax === '' ? null : Math.max(0, Number(newMax) || 0),
        expiresAt: newExpiry ? new Date(`${newExpiry}T23:59:59`).getTime() : undefined,
        commissionPlanId: newPlanId || undefined,
      });
      setNotice(`Link created — ${res?.link?.code}`);
      setAdding(false); setNewDealId(''); setNewMax(''); setNewExpiry(''); setNewPlanId('');
      await load();
    } catch (e: any) {
      // The server's message names the conflict (already has this deal, etc).
      setError(e?.message || 'Could not create the link');
    } finally { setBusy(false); }
  };

  /** Seed the editor from the link as it stands. */
  const openEdit = (link: any) => {
    setEditing(link);
    setEditMax(link.maxUses === null || link.maxUses === undefined ? '' : String(link.maxUses));
    // <input type="date"> wants YYYY-MM-DD in LOCAL time; toISOString would
    // shift the day backwards for anyone west of UTC.
    setEditExpiry(link.expiresAt ? new Date(link.expiresAt - new Date(link.expiresAt).getTimezoneOffset() * 60000)
      .toISOString().slice(0, 10) : '');
    setEditPlanId(link.commissionPlanId || '');
    setError(''); setNotice('');
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true); setError(''); setNotice('');
    try {
      await api.affiliates.admin.updateLink(editing.affiliateId || affiliateId, editing.linkId, {
        // '' means unlimited here; 0 is a real value meaning no seats.
        maxUses: editMax === '' ? null : Math.max(0, Number(editMax) || 0),
        // `null` clears an expiry rather than leaving the old one in place.
        expiresAt: editExpiry ? new Date(`${editExpiry}T23:59:59`).getTime() : null,
        commissionPlanId: editPlanId || null,
      });
      setNotice('Link updated');
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not update the link');
    } finally { setBusy(false); }
  };

  const patch = async (link: any, data: Record<string, any>) => {
    setBusy(true); setError('');
    try {
      await api.affiliates.admin.updateLink(affiliateId, link.linkId, data);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not update the link');
    } finally { setBusy(false); }
  };

  const remove = async (link: any) => {
    setBusy(true); setError('');
    try {
      await api.affiliates.admin.deleteLink(affiliateId, link.linkId);
      setNotice('Link deleted');
      await load();
    } catch (e: any) {
      // Refused once conversions exist — the message says to pause instead.
      setError(e?.message || 'Could not delete the link');
    } finally { setBusy(false); }
  };

  const dealOptions = useMemo(() => deals
    .filter((d) => !links.some((l) => l.dealId === d.id))
    .map((d) => ({
      value: d.id,
      label: d.name || d.code,
      subtitle: `${d.code}${typeof d.priceCents === 'number' ? ` · ${money(d.priceCents)}/mo` : ''}`,
    })), [deals, links]);

  /**
   * WHAT PAYS THIS LINK IF NOTHING IS PINNED.
   *
   * billing-service `pickPlan` (lib/commissionEmit.ts) resolves, in order:
   *   offer override -> THIS LINK'S PIN -> the deal's own plan -> the plan
   *   flagged default -> nothing at all, and "nothing" means no commission is
   *   recorded, not a fallback rate.
   *
   * The picker used to offer "Inherit the normal cascade", which named the
   * mechanism instead of the outcome: it did not say WHICH plan would pay, and
   * gave no hint that the answer could be "none, so this link pays nothing".
   * Resolved here so the empty choice states its own consequence.
   */
  const fallbackFor = useCallback((dealId?: string) => {
    const dealPlanId = deals.find((d) => d.id === dealId)?.commissionPlanId;
    const dealPlan = dealPlanId ? plans.find((p) => p.planId === dealPlanId) : undefined;
    if (dealPlan) return { name: dealPlan.name, source: "the deal's own plan", missing: false };
    const def = plans.find((p) => p.isDefault);
    if (def) return { name: def.name, source: 'the platform default plan', missing: false };
    return { name: 'nothing', source: 'no plan is configured', missing: true };
  }, [deals, plans]);

  const planOptionsFor = useCallback((dealId?: string) => {
    const fb = fallbackFor(dealId);
    return [
      {
        value: '',
        label: 'No plan pinned — use the fallback',
        subtitle: fb.missing
          ? 'Nothing would pay: no deal plan and no platform default'
          : `Pays ${fb.name}, from ${fb.source}`,
      },
      ...plans.filter((p) => p.status !== 'retired').map((p) => ({
        value: p.planId,
        label: p.name,
        subtitle: `${p.rules.length} rule${p.rules.length === 1 ? '' : 's'} · v${p.version}${p.isDefault ? ' · platform default' : ''}`,
      })),
    ];
  }, [plans, fallbackFor]);

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-700">
          <Check size={15} /> {notice}
        </div>
      )}

      {/* ---------------- deals ---------------- */}
      {showDeals && (
      <div className="rounded-2xl border border-stone-200 bg-white">
        <div className="flex items-center justify-between border-b border-stone-100 px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-stone-900">Deals &amp; links</h3>
            <p className="text-[11px] text-stone-500">
              One link per deal, each with its own code, seats and expiry — plus the
              standing referral link every partner always holds.
            </p>
          </div>
          <button
            onClick={() => setAdding((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700"
          >
            <Plus size={14} /> Assign a deal
          </button>
        </div>

        {adding && (
          <div className="border-b border-stone-100 bg-stone-50/60 p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <div className="md:col-span-2">
                <label className="mb-1 block text-[10px] font-semibold text-stone-500">Deal</label>
                <SearchableDropdown
                  options={dealOptions}
                  value={newDealId || null}
                  onChange={(v) => setNewDealId(v || '')}
                  placeholder={dealOptions.length ? 'Choose a deal' : 'Every active deal is already assigned'}
                  searchPlaceholder="Search deals…"
                  variant="light"
                  compact
                />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-semibold text-stone-500">Seats</label>
                <input
                  value={newMax}
                  onChange={(e) => setNewMax(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="Unlimited"
                  className="w-full rounded-lg border border-stone-300 bg-white px-2.5 py-2 text-xs font-mono text-stone-800 placeholder:text-stone-400"
                />
                <p className="mt-1 text-[10px] text-stone-400">Blank = unlimited</p>
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-semibold text-stone-500">Expires</label>
                <input
                  type="date"
                  value={newExpiry}
                  onChange={(e) => setNewExpiry(e.target.value)}
                  className="w-full rounded-lg border border-stone-300 bg-white px-2.5 py-2 text-xs text-stone-800"
                />
                <p className="mt-1 text-[10px] text-stone-400">Blank = never</p>
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-[10px] font-semibold text-stone-500">
                  Commission plan — what this link pays the partner
                </label>
                <SearchableDropdown
                  options={planOptionsFor(newDealId)}
                  value={newPlanId}
                  onChange={(v) => setNewPlanId(v || '')}
                  placeholder="Use the fallback"
                  variant="light"
                  compact
                />
                {!newPlanId && (
                  <p className={`mt-1 text-[10px] ${fallbackFor(newDealId).missing ? 'text-amber-600' : 'text-stone-400'}`}>
                    {fallbackFor(newDealId).missing
                      ? 'No deal plan and no platform default — this link would pay nothing.'
                      : `Falls back to ${fallbackFor(newDealId).name} (${fallbackFor(newDealId).source}).`}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={create}
                disabled={busy || !newDealId}
                className="rounded-lg bg-red-600 px-3.5 py-2 text-xs font-semibold text-white disabled:opacity-50"
              >
                {busy ? 'Creating…' : 'Create link'}
              </button>
              <button onClick={() => setAdding(false)} className="rounded-lg border border-stone-300 px-3 py-2 text-xs font-semibold text-stone-600">
                Cancel
              </button>
              <span className="ml-auto text-[10px] text-stone-400">
                A seat is used when a merchant PAYS — not when they sign up.
              </span>
            </div>
          </div>
        )}

        {loading ? (
          <RubyLoader variant="section" className="py-10" />
        ) : links.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <Link2 size={22} className="mx-auto mb-2 text-stone-300" />
            <p className="text-sm text-stone-600">No links</p>
            <p className="mt-1 text-xs text-stone-400">
              Not even the standing referral link loaded — assign a deal, or reload if the partner record is slow.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {links.map((l) => {
              const chip = STATUS_CHIP[l.displayStatus] || STATUS_CHIP.active;
              return (
                <div key={l.linkId} className={l.isBaseReferral ? 'bg-red-50/40' : ''}>
                <div className="flex flex-wrap items-center gap-3 px-5 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-stone-900">
                        {l.isBaseReferral
                          ? 'Standing referral link'
                          : (l.label || l.deal?.name || l.dealId)}
                      </span>
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                        l.isBaseReferral ? 'bg-red-100 text-red-700' : chip.cls
                      }`}>
                        {l.isBaseReferral ? 'Always on' : chip.label}
                      </span>
                    </div>
                    {/* Code and full share URL both copyable. The URL is the
                        thing a partner actually sends, and the two differ by
                        parameter — `?ref=` for the standing link, `?code=` for
                        a deal link — so it is taken from the server rather
                        than rebuilt here and silently got wrong. */}
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                      <button
                        onClick={() => copy(l.code, l.linkId)}
                        className="inline-flex items-center gap-1 font-mono text-[11px] text-stone-500 hover:text-red-600"
                        title="Copy code"
                      >
                        {l.code}
                        {copied === l.linkId ? <Check size={10} /> : <Copy size={10} className="opacity-60" />}
                      </button>
                      {l.shareUrl && (
                        <button
                          onClick={() => copy(l.shareUrl, `${l.linkId}:url`)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-stone-400 hover:text-red-600"
                          title={l.shareUrl}
                        >
                          {copied === `${l.linkId}:url` ? <Check size={10} /> : <Link2 size={10} />}
                          {copied === `${l.linkId}:url` ? 'Copied' : 'Copy link'}
                        </button>
                      )}
                    </div>

                    {/* The standing link's own terms, on the row that IS the
                        standing link. They used to be two more cards further
                        down the page, which made the always-on code look like a
                        different mechanism from the deal links above it. */}
                    {l.isBaseReferral && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
                        <span className="text-stone-500">
                          Sells{' '}
                          <b className="text-stone-800">{standingDealLabel || 'the default deal'}</b>
                          {onChangeStandingDeal && (
                            <button
                              onClick={onChangeStandingDeal}
                              className="ml-1.5 font-semibold text-red-600 hover:text-red-700"
                            >
                              Change
                            </button>
                          )}
                        </span>
                        {standingCommissionLabel && (
                          <span className="text-stone-500">
                            Pays <b className="text-stone-800">{standingCommissionLabel}</b>
                            {onEditCommission && (
                              <button
                                onClick={onEditCommission}
                                className="ml-1.5 font-semibold text-red-600 hover:text-red-700"
                              >
                                Edit
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="text-right text-[11px] text-stone-500">
                    <div>
                      {l.isBaseReferral
                        ? 'No cap'
                        : l.remainingUses === null
                          ? 'Unlimited'
                          : <><b className="text-stone-800">{l.remainingUses}</b> of {l.maxUses} left</>}
                    </div>
                    <div>{l.usedCount || 0} paid · {l.signupCount || 0} signups</div>
                  </div>

                  <div className="text-right text-[11px] text-stone-500 w-24">
                    <div>{l.expiresAt ? dateStr(l.expiresAt) : 'No expiry'}</div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    {/* The standing referral is the partner's identity, not a
                        campaign: it has no cap, no expiry, and cannot be paused,
                        edited or deleted. Admin sees it here only so the two
                        screens agree about what the partner holds. */}
                    <button
                      onClick={() => (editing?.linkId === l.linkId ? setEditing(null) : openEdit(l))}
                      disabled={busy || l.isBaseReferral}
                      className="rounded-lg border border-stone-200 p-1.5 text-stone-500 hover:border-stone-300 disabled:opacity-30"
                      title={l.isBaseReferral ? 'The standing link has no seats or expiry to edit' : 'Edit seats, expiry and commission plan'}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => patch(l, { status: l.status === 'paused' ? 'active' : 'paused' })}
                      disabled={busy || l.isBaseReferral}
                      className="rounded-lg border border-stone-200 p-1.5 text-stone-500 hover:border-stone-300 disabled:opacity-30"
                      title={l.status === 'paused' ? 'Resume' : 'Pause'}
                    >
                      {l.status === 'paused' ? <Play size={13} /> : <Pause size={13} />}
                    </button>
                    <button
                      onClick={() => remove(l)}
                      disabled={busy || l.isBaseReferral || (l.usedCount || 0) > 0}
                      className="rounded-lg border border-stone-200 p-1.5 text-stone-400 hover:border-red-300 hover:text-red-600 disabled:opacity-30"
                      title={(l.usedCount || 0) > 0 ? 'Has conversions — pause instead' : 'Delete'}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                {editing?.linkId === l.linkId && (
                  <div className="border-t border-stone-100 bg-stone-50/60 px-5 py-4">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                      <div>
                        <label className="mb-1 block text-[10px] font-semibold text-stone-500">Seats</label>
                        <input
                          value={editMax}
                          onChange={(e) => setEditMax(e.target.value.replace(/[^0-9]/g, ''))}
                          placeholder="Unlimited"
                          className="w-full rounded-lg border border-stone-300 bg-white px-2.5 py-2 font-mono text-xs text-stone-800 placeholder:text-stone-400"
                        />
                        <p className="mt-1 text-[10px] text-stone-400">
                          Blank = unlimited · {l.usedCount || 0} already used
                        </p>
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] font-semibold text-stone-500">Expires</label>
                        <input
                          type="date"
                          value={editExpiry}
                          onChange={(e) => setEditExpiry(e.target.value)}
                          className="w-full rounded-lg border border-stone-300 bg-white px-2.5 py-2 text-xs text-stone-800"
                        />
                        <p className="mt-1 text-[10px] text-stone-400">Blank = never</p>
                      </div>
                      <div className="md:col-span-2">
                        <label className="mb-1 block text-[10px] font-semibold text-stone-500">
                          Commission plan — what this link pays the partner
                        </label>
                        <SearchableDropdown
                          options={planOptionsFor(l.dealId)}
                          value={editPlanId}
                          onChange={(v) => setEditPlanId(v || '')}
                          placeholder="Use the fallback"
                          variant="light"
                          compact
                        />
                        <p className={`mt-1 text-[10px] ${
                          !editPlanId && fallbackFor(l.dealId).missing ? 'text-amber-600' : 'text-stone-400'
                        }`}>
                          {editPlanId
                            ? 'Pinned to this link — it wins over the deal’s plan and the platform default.'
                            : fallbackFor(l.dealId).missing
                              ? 'No deal plan and no platform default — this link pays nothing.'
                              : `Falls back to ${fallbackFor(l.dealId).name} (${fallbackFor(l.dealId).source}).`}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        onClick={saveEdit}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3.5 py-2 text-xs font-semibold text-white disabled:opacity-50"
                      >
                        {busy ? <><RubyGemMark size={12} drawing color="currentColor" /> Saving…</> : 'Save changes'}
                      </button>
                      <button
                        onClick={() => setEditing(null)}
                        className="rounded-lg border border-stone-300 px-3 py-2 text-xs font-semibold text-stone-600"
                      >
                        Cancel
                      </button>
                      {/* The deal is the one thing an edit cannot change — see
                          the `editing` comment above. */}
                      <span className="ml-auto text-[10px] text-stone-400">
                        The deal itself cannot be changed — add a second link instead.
                      </span>
                    </div>
                  </div>
                )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* ---------------- commissions ---------------- */}
      {showCommissions && (
      <div className="rounded-2xl border border-stone-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-100 px-5 py-4">
          <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-1.5">
            <Percent size={14} /> Commissions
          </h3>
          <div className="flex items-center gap-1.5">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`rounded-lg px-2.5 py-1.5 text-[11px] font-semibold ${
                  period === p.key ? 'bg-red-600 text-white' : 'border border-stone-200 text-stone-600 hover:border-stone-300'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-px bg-stone-100 md:grid-cols-4">
          {[
            { label: 'Earned', value: ledger?.summary?.totalCents || 0 },
            { label: 'Clearing', value: ledger?.summary?.byStatus?.clearing?.totalCents || 0 },
            { label: 'Cleared', value: ledger?.summary?.byStatus?.cleared?.totalCents || 0 },
            { label: 'Lifetime', value: ledger?.lifetime?.totalCents || 0 },
          ].map((c) => (
            <div key={c.label} className="bg-white px-5 py-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">{c.label}</div>
              <div className="mt-0.5 font-mono text-lg font-bold text-stone-900">{money(c.value)}</div>
            </div>
          ))}
        </div>

        {!ledger?.events?.length ? (
          <p className="px-5 py-8 text-center text-xs text-stone-400">
            No commission entries in this period.
          </p>
        ) : (
          <div className="max-h-80 overflow-y-auto divide-y divide-stone-50">
            {ledger.events.slice(0, 50).map((e: any) => (
              <div key={e.eventId} className="flex items-center gap-3 px-5 py-2.5 text-xs">
                <span className="w-20 shrink-0 text-stone-400">{dateStr(e.occurredAt)}</span>
                <span className="min-w-0 flex-1 truncate text-stone-700">{e.sourceLabel || e.sourceRef}</span>
                {/* The receipt — what a payment query is traced through. */}
                <span className="hidden shrink-0 font-mono text-[10px] text-stone-400 md:block">
                  {e.planId} · v{e.planVersion}
                </span>
                <span className="w-16 shrink-0 text-right text-stone-500">{e.status}</span>
                <span className={`w-20 shrink-0 text-right font-mono font-semibold ${e.commissionCents < 0 ? 'text-stone-400' : 'text-stone-900'}`}>
                  {money(e.commissionCents)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default PartnerDealsPanel;
